import { Container } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { getHistoryDisplayTranslations, useRecapTranslationsQuery } from '../../../services/HistoryPageServices';
import calendarIcon from '../../../assets/images/icon/icon-calendar.svg';
import locationIcon from '../../../assets/images/icon/icon-location.svg';
import penIcon from '../../../assets/images/icon/icon-pen.svg';

interface HistoryDisplayCardProps {
  align: 'left' | 'right';
  index: number;
  priority?: boolean;
}

interface HistoryDisplayIntroProps {
  legacySectionName: string;
  displayTitle: string;
  displayDescription: string;
  className?: string;
}

function hasTranslation(value: string, key: string) {
  return value && value !== key;
}

function formatTheme(theme: string) {
  return theme.trim();
}

function HistoryDisplayIntro({ legacySectionName, displayTitle, displayDescription, className = '' }: HistoryDisplayIntroProps) {
  return (
    <div className={`history-display__intro${className ? ` ${className}` : ''}`}>
      {hasTranslation(legacySectionName, 'legacy_section_name') ? (
        <div className="history-display__heading">{legacySectionName}</div>
      ) : null}
      {hasTranslation(displayTitle, 'display_title') ? (
        <div className="history-display__title">{displayTitle}</div>
      ) : null}
      {hasTranslation(displayDescription, 'display_description') ? (
        <div className="history-display__description">{displayDescription}</div>
      ) : null}
    </div>
  );
}

function HistoryDisplayCard({ align, index, priority = false }: HistoryDisplayCardProps) {
  const { t } = useTranslation('recap_display');
  const themeKey = `display_card_theme_${index}`;
  const imageKey = `display_card_image_${index}`;
  const descriptionKey = `display_card_description_${index}`;
  const dateKey = `display_card_date_${index}`;
  const locationKey = `display_card_location_${index}`;
  const linkKey = `display_card_link_${index}`;
  const titleKey = `display_card_title_${index}`;
  const theme = t(themeKey);
  const description = t(descriptionKey);
  const date = t(dateKey);
  const location = t(locationKey);
  const link = t(linkKey);
  const title = t(titleKey);
  const cardClassName = `history-display-card history-display-card--${align}`;
  const hasLink = hasTranslation(link, linkKey);
  const cardContent = (
    <>
      <img
        className="history-display-card__image"
        src={t(imageKey)}
        alt={hasTranslation(title, titleKey) ? title : ''}
        loading={priority ? 'eager' : 'lazy'}
        decoding="async"
        fetchPriority={priority ? 'high' : 'auto'}
      />
      <div className="history-display-card__content-wrap">
        <div className="history-display-card__content">
          <div className="history-display-card__copy">
            {hasTranslation(title, titleKey) ? (
              <div className="history-display-card__title">{title}</div>
            ) : null}
            {hasTranslation(description, descriptionKey) ? (
              <div className="history-display-card__description">{description}</div>
            ) : null}
          </div>
          {(hasTranslation(theme, themeKey) || hasTranslation(date, dateKey) || hasTranslation(location, locationKey)) ? (
            <div className="history-display-card__meta">
              {hasTranslation(theme, themeKey) ? (
                <div className="history-display-card__meta-item">
                  <img src={penIcon} alt="" aria-hidden="true" />
                  <span>{formatTheme(theme)}</span>
                </div>
              ) : null}
              {hasTranslation(date, dateKey) ? (
                <div className="history-display-card__meta-item">
                  <img src={calendarIcon} alt="" aria-hidden="true" />
                  <span>{date}</span>
                </div>
              ) : null}
              {hasTranslation(location, locationKey) ? (
                <div className="history-display-card__meta-item">
                  <img src={locationIcon} alt="" aria-hidden="true" />
                  <span>{location}</span>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </>
  );

  if (hasLink) {
    return (
      <a className={cardClassName} href={link}>
        {cardContent}
      </a>
    );
  }

  return (
    <article className={cardClassName}>
      {cardContent}
    </article>
  );
}

function HistoryDisplay() {
  const { t } = useTranslation('recap_display');
  const legacySectionName = t('legacy_section_name');
  const displayTitle = t('display_title');
  const displayDescription = t('display_description');
  const recapQuery = useRecapTranslationsQuery();
  const displayCardIndexes = recapQuery.data ? getHistoryDisplayTranslations(recapQuery.data) : undefined;
  const isLoading = recapQuery.isLoading;

  if (!displayCardIndexes || isLoading) return null;

  const sortedDisplayCardIndexes = [...displayCardIndexes].sort((currentIndex, nextIndex) => {
    return getDisplayCardYear(nextIndex, t) - getDisplayCardYear(currentIndex, t);
  });
  const leftColumnIndexes = sortedDisplayCardIndexes.filter((_, index) => index % 2 === 0);
  const rightColumnIndexes = sortedDisplayCardIndexes.filter((_, index) => index % 2 === 1);

  return (
    <section className="history-display">
      <div className="history-display__gradient" aria-hidden="true"></div>
      <Container className="history-display__container">
        <HistoryDisplayIntro
          legacySectionName={legacySectionName}
          displayTitle={displayTitle}
          displayDescription={displayDescription}
          className="history-display__intro--mobile"
        />
        <div className="history-display__cards-mobile">
          {sortedDisplayCardIndexes.map((index, displayIndex) => (
            <HistoryDisplayCard key={`history_display_mobile_card_${index}`} align={displayIndex % 2 === 0 ? 'left' : 'right'} index={index} priority={displayIndex === 0} />
          ))}
        </div>
        <div className="history-display__column history-display__column--left history-display__column--desktop">
          {leftColumnIndexes.map((index, columnIndex) => (
            <HistoryDisplayCard key={`history_display_card_${index}`} align="left" index={index} priority={columnIndex === 0} />
          ))}
        </div>
        <div className="history-display__column history-display__column--right history-display__column--desktop">
          <HistoryDisplayIntro
            legacySectionName={legacySectionName}
            displayTitle={displayTitle}
            displayDescription={displayDescription}
            className="history-display__intro--desktop"
          />
          {rightColumnIndexes.map((index, columnIndex) => (
            <HistoryDisplayCard key={`history_display_card_${index}`} align="right" index={index} priority={columnIndex === 0} />
          ))}
        </div>
      </Container>
    </section>
  );
}

function getDisplayCardYear(index: number, translate: (key: string) => string) {
  const year = translate(`display_card_year_${index}`);
  const title = translate(`display_card_title_${index}`);
  const date = translate(`display_card_date_${index}`);

  return Number(`${year} ${title} ${date}`.match(/\d{4}/)?.[0] ?? 0);
}

export default HistoryDisplay;
